import React, { useState, useEffect } from 'react';
import { 
  QrCode, Plus, Trash2, Edit2, Eye, Download, Copy, 
  Star, ThumbsUp, ThumbsDown, Scan, ExternalLink, Check, Printer
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import QRCode from 'react-qr-code';

const MyMagicQR = () => {
  const [qrCodes, setQRCodes] = useState([]);
  const [limits, setLimits] = useState({ count: 0, limit: 1, can_create: false });
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingQR, setEditingQR] = useState(null);
  const [showQRModal, setShowQRModal] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(null);
  const [feedbackList, setFeedbackList] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await api.get('/magic-qr/my-qr-codes');
      setQRCodes(res.data.qr_codes);
      setLimits({
        count: res.data.count,
        limit: res.data.limit,
        can_create: res.data.can_create
      });
    } catch (error) {
      console.error('Failed to fetch QR codes:', error);
    } finally {
      setLoading(false);
    }
  };

  const viewFeedback = async (qrId) => {
    try {
      const res = await api.get(`/magic-qr/qr/${qrId}/feedback`);
      setFeedbackList(res.data);
      setShowFeedbackModal(qrId);
    } catch (error) {
      toast.error('Failed to load feedback');
    }
  };

  const deleteQR = async (qrId) => {
    if (!confirm('Delete this QR code?')) return;
    try {
      await api.delete(`/magic-qr/qr/${qrId}`);
      toast.success('QR code deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete QR code');
    }
  };

  const copyLink = (code) => {
    navigator.clipboard.writeText(`${window.location.origin}/review/${code}`);
    toast.success('Link copied to clipboard!');
  };

  const downloadQR = (code, businessName) => {
    const svg = document.getElementById(`qr-${code}`);
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = 300;
      canvas.height = 300;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, 300, 300);
      ctx.drawImage(img, 25, 25, 250, 250);
      
      const link = document.createElement('a');
      link.download = `magic-qr-${businessName.replace(/\s+/g, '-').toLowerCase()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  const printQR = (code, businessName, businessLocation) => {
    const printWindow = window.open('', '_blank');
    const qrUrl = `${window.location.origin}/review/${code}`;
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Magic QR - ${businessName}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: Arial, sans-serif; 
            display: flex; 
            justify-content: center; 
            align-items: center; 
            min-height: 100vh;
            background: #f5f5f5;
          }
          .qr-card {
            background: white;
            padding: 40px;
            border-radius: 20px;
            box-shadow: 0 10px 40px rgba(0,0,0,0.1);
            text-align: center;
            max-width: 400px;
          }
          .logo { font-size: 24px; font-weight: bold; color: #D4AF37; margin-bottom: 10px; }
          .business-name { font-size: 28px; font-weight: bold; margin-bottom: 5px; }
          .business-location { color: #666; margin-bottom: 20px; }
          .qr-container { 
            background: white; 
            padding: 20px; 
            border-radius: 15px; 
            display: inline-block;
            margin-bottom: 20px;
          }
          .scan-text { font-size: 18px; color: #333; margin-bottom: 10px; }
          .stars { color: #FFD700; font-size: 24px; margin-bottom: 15px; }
          .url { font-size: 12px; color: #999; word-break: break-all; }
          .footer { margin-top: 20px; font-size: 12px; color: #999; }
          @media print {
            body { background: white; }
            .qr-card { box-shadow: none; }
          }
        </style>
      </head>
      <body>
        <div class="qr-card">
          <div class="logo">ANANT Magic Tool</div>
          <div class="business-name">${businessName}</div>
          <div class="business-location">${businessLocation}</div>
          <div class="qr-container">
            <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrUrl)}" alt="QR Code" />
          </div>
          <div class="scan-text">Scan to leave a review</div>
          <div class="stars">★★★★★</div>
          <div class="url">${qrUrl}</div>
          <div class="footer">Powered by ANANT WEB PORTAL</div>
        </div>
        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="my-magic-qr">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <QrCode className="w-8 h-8 text-primary" />
              ANANT Magic Tool
            </h1>
            <p className="text-muted-foreground">Create Magic QR codes for smart review collection</p>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="px-4 py-2 bg-white/5 rounded-lg">
              <span className="text-sm">QR Codes: </span>
              <span className="font-bold">{limits.count}</span>
              <span className="text-muted-foreground">/{limits.limit}</span>
            </div>
            
            {limits.can_create ? (
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn-primary flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Create QR Code
              </button>
            ) : (
              <div className="px-4 py-2 bg-red-500/20 text-red-500 rounded-lg text-sm">
                Limit reached. Upgrade to create more.
              </div>
            )}
          </div>
        </div>

        {/* How it Works */}
        <div className="glass-card p-6 mb-8">
          <h3 className="text-lg font-semibold mb-4">How Magic QR Works</h3>
          <div className="grid md:grid-cols-4 gap-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-primary/20 text-primary rounded-full flex items-center justify-center font-bold">1</div>
              <div>
                <p className="font-medium">Customer Scans</p>
                <p className="text-sm text-muted-foreground">Customer scans QR at your business</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-primary/20 text-primary rounded-full flex items-center justify-center font-bold">2</div>
              <div>
                <p className="font-medium">5-Star Default</p>
                <p className="text-sm text-muted-foreground">Pre-selected 5-star with AI praise</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-green-500/20 text-green-500 rounded-full flex items-center justify-center font-bold">3a</div>
              <div>
                <p className="font-medium">Happy Customer</p>
                <p className="text-sm text-muted-foreground">5-star → Redirects to Google Reviews</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-yellow-500/20 text-yellow-500 rounded-full flex items-center justify-center font-bold">3b</div>
              <div>
                <p className="font-medium">Unhappy Customer</p>
                <p className="text-sm text-muted-foreground">Below 5-star → Feedback to your dashboard</p>
              </div>
            </div>
          </div>
        </div>

        {/* QR Codes Grid */}
        {qrCodes.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {qrCodes.map(qr => (
              <div key={qr.id} className="glass-card p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-bold text-lg">{qr.business_name}</h3>
                    <p className="text-sm text-muted-foreground">{qr.business_location}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs ${qr.is_active ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>
                    {qr.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {/* QR Code Preview */}
                <div 
                  className="flex justify-center my-4 p-4 bg-white rounded-xl cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => setShowQRModal(qr)}
                >
                  <QRCode 
                    id={`qr-${qr.code}`}
                    value={`${window.location.origin}/review/${qr.code}`} 
                    size={150} 
                  />
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-2 text-center mb-4">
                  <div className="p-3 bg-white/5 rounded-lg">
                    <Scan className="w-5 h-5 mx-auto mb-1 text-blue-500" />
                    <p className="text-xl font-bold">{qr.scan_count}</p>
                    <p className="text-xs text-muted-foreground">Scans</p>
                  </div>
                  <div className="p-3 bg-white/5 rounded-lg">
                    <ThumbsUp className="w-5 h-5 mx-auto mb-1 text-green-500" />
                    <p className="text-xl font-bold text-green-500">{qr.positive_count}</p>
                    <p className="text-xs text-muted-foreground">Positive</p>
                  </div>
                  <div className="p-3 bg-white/5 rounded-lg">
                    <ThumbsDown className="w-5 h-5 mx-auto mb-1 text-red-500" />
                    <p className="text-xl font-bold text-red-500">{qr.negative_count}</p>
                    <p className="text-xs text-muted-foreground">Negative</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => viewFeedback(qr.id)}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-primary/20 text-primary rounded-lg hover:bg-primary/30"
                  >
                    <Eye className="w-4 h-4" />
                    Feedback
                  </button>
                  <button
                    onClick={() => printQR(qr.code, qr.business_name, qr.business_location)}
                    className="px-3 py-2 bg-blue-500/20 text-blue-500 rounded-lg hover:bg-blue-500/30"
                    title="Print QR"
                  >
                    <Printer className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => copyLink(qr.code)}
                    className="px-3 py-2 bg-white/5 rounded-lg hover:bg-white/10"
                    title="Copy Link"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => downloadQR(qr.code, qr.business_name)}
                    className="px-3 py-2 bg-green-500/20 text-green-500 rounded-lg hover:bg-green-500/30"
                    title="Download QR"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setEditingQR(qr)}
                    className="px-3 py-2 bg-white/5 rounded-lg hover:bg-white/10"
                    title="Edit"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deleteQR(qr.id)}
                    className="px-3 py-2 bg-red-500/20 text-red-500 rounded-lg hover:bg-red-500/30"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="glass-card p-12 text-center">
            <QrCode className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Magic QR Codes Yet</h3>
            <p className="text-muted-foreground mb-6">
              Create your first Magic QR to start collecting smart reviews!
            </p>
            {limits.can_create && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn-primary"
              >
                Create Your First QR Code
              </button>
            )}
          </div>
        )}

        {/* Create/Edit Modal */}
        {(showCreateModal || editingQR) && (
          <CreateQRModal
            editData={editingQR}
            onClose={() => {
              setShowCreateModal(false);
              setEditingQR(null);
            }}
            onSuccess={() => {
              setShowCreateModal(false);
              setEditingQR(null);
              fetchData();
            }}
          />
        )}

        {/* QR View Modal */}
        {showQRModal && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
            <div className="glass-card p-8 max-w-md w-full text-center">
              <h3 className="text-xl font-bold mb-2">{showQRModal.business_name}</h3>
              <p className="text-muted-foreground mb-6">{showQRModal.business_location}</p>
              
              <div className="bg-white p-6 rounded-xl inline-block mb-6">
                <QRCode 
                  value={`${window.location.origin}/review/${showQRModal.code}`} 
                  size={250} 
                />
              </div>
              
              <p className="text-sm text-muted-foreground mb-4">
                Scan to leave a review
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => copyLink(showQRModal.code)}
                  className="flex-1 btn-primary flex items-center justify-center gap-2"
                >
                  <Copy className="w-4 h-4" />
                  Copy Link
                </button>
                <button
                  onClick={() => downloadQR(showQRModal.code, showQRModal.business_name)}
                  className="flex-1 px-4 py-2 bg-white/10 rounded-full flex items-center justify-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
              </div>
              
              <button
                onClick={() => setShowQRModal(null)}
                className="mt-4 w-full px-4 py-2 bg-white/5 rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Feedback Modal */}
        {showFeedbackModal && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
            <div className="glass-card p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold">Customer Feedback</h3>
                <button
                  onClick={() => setShowFeedbackModal(null)}
                  className="p-2 hover:bg-white/10 rounded-lg"
                >
                  ×
                </button>
              </div>
              
              {feedbackList.length > 0 ? (
                <div className="space-y-4">
                  {feedbackList.map((fb, idx) => (
                    <div key={idx} className={`p-4 rounded-lg ${fb.is_positive ? 'bg-green-500/10 border border-green-500/20' : 'bg-red-500/10 border border-red-500/20'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="flex">
                            {[1,2,3,4,5].map(star => (
                              <Star 
                                key={star} 
                                className={`w-4 h-4 ${star <= fb.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-600'}`} 
                              />
                            ))}
                          </div>
                          {fb.sent_to_google && (
                            <span className="px-2 py-0.5 bg-green-500/20 text-green-500 rounded text-xs">
                              Sent to Google
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {new Date(fb.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      
                      <div className="mb-2">
                        <p className="font-medium">{fb.name}</p>
                        <p className="text-sm text-muted-foreground">{fb.phone} {fb.email && `• ${fb.email}`}</p>
                      </div>
                      
                      {fb.issue_category && (
                        <span className="inline-block px-2 py-1 bg-white/10 rounded text-xs mb-2">
                          {fb.issue_category}
                        </span>
                      )}
                      
                      <p className="text-sm">{fb.feedback_text}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No feedback received yet
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Create/Edit QR Modal Component
const CreateQRModal = ({ editData, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    business_name: editData?.business_name || '',
    business_location: editData?.business_location || '',
    google_review_url: editData?.google_review_url || '',
    theme_color: editData?.theme_color || '#D4AF37',
    custom_praise_quotes: editData?.custom_praise_quotes || []
  });
  const [loading, setLoading] = useState(false);
  const [newQuote, setNewQuote] = useState('');

  const handleSubmit = async () => {
    if (!formData.business_name || !formData.business_location) {
      toast.error('Please fill in business name and location');
      return;
    }

    setLoading(true);
    try {
      if (editData) {
        await api.put(`/magic-qr/qr/${editData.id}`, formData);
        toast.success('QR code updated!');
      } else {
        await api.post('/magic-qr/create', formData);
        toast.success('Magic QR created!');
      }
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save QR code');
    } finally {
      setLoading(false);
    }
  };

  const addQuote = () => {
    if (newQuote.trim()) {
      setFormData({
        ...formData,
        custom_praise_quotes: [...formData.custom_praise_quotes, newQuote.trim()]
      });
      setNewQuote('');
    }
  };

  const removeQuote = (index) => {
    setFormData({
      ...formData,
      custom_praise_quotes: formData.custom_praise_quotes.filter((_, i) => i !== index)
    });
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
      <div className="glass-card p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-bold mb-6">
          {editData ? 'Edit Magic QR' : 'Create Magic QR'}
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Business Name *</label>
            <input
              type="text"
              value={formData.business_name}
              onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
              placeholder="e.g., My Restaurant"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Business Location *</label>
            <input
              type="text"
              value={formData.business_location}
              onChange={(e) => setFormData({ ...formData, business_location: e.target.value })}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
              placeholder="e.g., Mumbai, Maharashtra"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Google Review URL (Optional)</label>
            <input
              type="url"
              value={formData.google_review_url}
              onChange={(e) => setFormData({ ...formData, google_review_url: e.target.value })}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
              placeholder="https://g.page/r/..."
              data-testid="google-review-url-input"
            />
            <div className="mt-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-xs space-y-1">
              <p className="font-semibold text-blue-400">📍 सही Google Review Link कैसे लें:</p>
              <ol className="list-decimal list-inside text-muted-foreground space-y-1 ml-1">
                <li><a href="https://business.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 underline">business.google.com</a> पर login करें</li>
                <li>अपना Business select करें → <strong>"Read Reviews"</strong> या <strong>"Get more reviews"</strong> पर click करें</li>
                <li><strong>"Share review form"</strong> button दबाएं → link copy करें (जैसे: <code>https://g.page/r/CXXXX/review</code>)</li>
                <li>वो link यहाँ paste करें</li>
              </ol>
              <p className="text-yellow-400 mt-2">
                ⚠️ <strong>अगर खाली छोड़ा</strong> तो Google Maps पर business name से search होगा (less direct, पर काम करेगा)।
              </p>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Theme Color</label>
            <div className="flex gap-2">
              <input
                type="color"
                value={formData.theme_color}
                onChange={(e) => setFormData({ ...formData, theme_color: e.target.value })}
                className="w-12 h-10 rounded cursor-pointer"
              />
              <input
                type="text"
                value={formData.theme_color}
                onChange={(e) => setFormData({ ...formData, theme_color: e.target.value })}
                className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Custom Praise Quotes (Optional)</label>
            <p className="text-xs text-muted-foreground mb-2">
              Add your own praise quotes that customers can use. We'll randomly pick one.
            </p>
            
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newQuote}
                onChange={(e) => setNewQuote(e.target.value)}
                className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                placeholder="Add a praise quote..."
              />
              <button
                onClick={addQuote}
                className="px-4 py-2 bg-primary/20 text-primary rounded-lg"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            
            {formData.custom_praise_quotes.length > 0 && (
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {formData.custom_praise_quotes.map((quote, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 bg-white/5 rounded-lg">
                    <p className="flex-1 text-sm truncate">{quote}</p>
                    <button
                      onClick={() => removeQuote(idx)}
                      className="text-red-500 hover:text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <div className="flex gap-3 mt-6">
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 btn-primary disabled:opacity-50"
          >
            {loading ? 'Saving...' : (editData ? 'Update QR' : 'Create QR')}
          </button>
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-white/5 rounded-full"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default MyMagicQR;
